import { ref, watch, type Ref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import ApiService from '@/services/api.service'

interface EmailCheckResponse {
  available: boolean
  message?: string
}

export function useEmailAvailability(email: Ref<string | undefined>) {
  const serverError = ref<string | undefined>(undefined)
  const debouncedEmail = ref<string | undefined>(undefined)
  const debounceTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

  // Basic email validation
  const isValidEmailFormat = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Debounce email changes
  watch(email, (newEmail) => {
    // Clear any existing timeout
    if (debounceTimeout.value) {
      clearTimeout(debounceTimeout.value)
    }

    // Clear error if email is empty
    if (!newEmail) {
      serverError.value = undefined
      debouncedEmail.value = undefined
      return
    }

    // Don't check if format is invalid (let Zod handle it)
    if (!isValidEmailFormat(newEmail)) {
      serverError.value = undefined
      debouncedEmail.value = undefined
      return
    }

    // Debounce the email check (500ms after user stops typing)
    debounceTimeout.value = setTimeout(() => {
      debouncedEmail.value = newEmail
    }, 500)
  })

  // TanStack Query for email availability check
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['email-availability', debouncedEmail],
    queryFn: async () => {
      if (!debouncedEmail.value) {
        return null
      }

      try {
        const response = await ApiService.post('/v2/expert/check-email', {
          email: debouncedEmail.value
        })
        return response.data as EmailCheckResponse
      } catch (err: any) {
        // Handle error responses
        if (err.response?.status === 422 || err.response?.data?.available === false) {
          return {
            available: false,
            message: err.response?.data?.message || 'This email is already registered. Please use a different email or login to your account.'
          }
        }
        throw err
      }
    },
    enabled: () => !!debouncedEmail.value,
    retry: false, // Don't retry on failure
    staleTime: 30000, // Cache for 30 seconds
    gcTime: 60000, // Keep in cache for 1 minute
  })

  // Update server error based on query result
  watch([data, error], ([newData, newError]) => {
    if (newError) {
      // Network or unexpected error - don't show to user, let Zod validation handle it
      serverError.value = undefined
      return
    }

    if (newData) {
      if (newData.available === false) {
        serverError.value = newData.message || 'This email is already registered. Please use a different email or login to your account.'
      } else {
        serverError.value = undefined
      }
    }
  })

  return {
    serverError,
    isChecking: isLoading,
    refetch,
  }
}

